import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Eye,
  Sun,
  KeyRound,
  Scale,
  Boxes,
  Milestone,
  BookMarked,
  ArrowLeftRight,
  ClipboardCheck,
  MessagesSquare,
  FolderKanban,
  CalendarDays,
  HardHat,
  Package,
  BookUser,
  Settings,
  Wand2,
  Calculator,
  Megaphone,
  Menu,
  LogOut,
  Palette,
  Images,
  Banknote,
  Table2,
  Truck,
  ShoppingCart,
  ShieldOff,
  Database,
  FileCheck2,
  Inbox,
  Smartphone,
  NotebookPen,
  FileQuestion,
  Mails,
  DraftingCompass,
  UserCog,
  Search as SearchIcon,
  LifeBuoy,
  Bell,
  CheckCheck,
  MessageCircle,
  BookOpenCheck,
  LayoutDashboard,
  Users,
  Hammer,
  Wallet,
  Handshake,
  Pin,
  PinOff,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronRight,
  FileSpreadsheet,
  FileSignature,
  Building2,
  ArrowRightLeft,
  Undo2,
  Loader2,
  ScrollText,
  Ruler,
  MessageSquareShare, LayoutTemplate, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isAdminLike, isReadOnlyUser, READ_ONLY_MESSAGE } from "@/lib/roles";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CommandPalette } from "@/components/common/CommandPalette";
import { OnboardingChecklist } from "@/components/common/OnboardingChecklist";
import { LastSyncedBadge } from "@/components/financials/LastSyncedBadge";
import { ActingBanner } from "@/components/landlord/ActingBanner";
import { XeroReconnectBanner } from "@/components/shared/XeroReconnectBanner";
import { BankFeedReconnectBanner } from "@/components/shared/BankFeedReconnectBanner";
import { useAuth } from "@/context/AuthContext";
import { BuiltBuddyGlobalLauncher } from "@/components/builtbuddy/BuiltBuddyGlobalLauncher";
import { markAllNotificationsRead, markNotificationRead, useNotifications } from "@/lib/notifications";
import { ThemePickerDialog } from "@/components/common/ThemePicker";
import { useColorTheme } from "@/hooks/useColorTheme";
import { useBranding } from "@/hooks/useBranding";
import { BuilderLogo, BrandWatermark, PoweredByBuiltOS, BuiltOSWordmark } from "@/components/common/BuilderLogo";
import { api } from "@/lib/api";
import { initials } from "@/lib/format";

// ---------------------------------------------------------------------------
// Navigation model
// The rail shows one icon per department, in the order work flows through the
// business: Dashboard → CRM → Estimating → Contracts → Jobs → Financials → Partners.
// Clicking a department icon lands on that department's main page (`home`); the
// contextual panel shows only its pages, arranged in small sub-sections that
// follow the order work actually happens (find it → do it → reference).
// `perm` maps each page to the per-user permission matrix (Settings → Team).
// ---------------------------------------------------------------------------
const NAV_GROUPS = [
  {
    key: "home",
    label: "Dashboard",
    hint: "Today, approvals & messages",
    icon: LayoutDashboard,
    home: "/",
    testId: "nav-rail-group-home",
    sections: [
      {
        items: [
          { to: "/", label: "Today", icon: Sun, testId: "sidebar-nav-dashboard", end: true, badge: "today" },
          { to: "/approvals", label: "Approvals", icon: ClipboardCheck, testId: "sidebar-nav-approvals", badge: "approvals", perm: "approvals" },
          { to: "/comms", label: "Comms Hub", icon: MessagesSquare, testId: "sidebar-nav-comms", perm: "jobs" },
        ],
      },
    ],
  },
  {
    key: "win",
    label: "CRM",
    hint: "Leads, pipeline & clients",
    icon: Users,
    home: "/crm",
    testId: "nav-rail-group-win",
    sections: [
      {
        label: "Pipeline",
        items: [
          { to: "/crm", label: "Pipeline & CRM", icon: Megaphone, testId: "sidebar-nav-crm", perm: "crm" },
          { to: "/clients", label: "Clients", icon: BookUser, testId: "sidebar-nav-clients", perm: "clients" },
          { to: "/client-updates", label: "Client Updates", icon: MessageSquareShare, testId: "sidebar-nav-client-updates", perm: "clients", sub: true },
        ],
      },
    ],
  },
  {
    key: "estimate",
    label: "Estimating",
    hint: "Quick quotes, takeoffs, BOQs & rates",
    icon: Calculator,
    home: "/boq",
    testId: "nav-rail-group-estimate",
    sections: [
      {
        label: "Estimate",
        items: [
          { to: "/quick-quote", label: "Quick Quote", icon: Wand2, testId: "sidebar-nav-quick-quote", perm: "quick_quote" },
          { to: "/boq", label: "BOQ", icon: Ruler, testId: "sidebar-nav-boq", perm: "boq" },
          { to: "/rate-book", label: "Rate Book", icon: BookMarked, testId: "sidebar-nav-rate-book", perm: "quotes" },
          { to: "/boq-templates", label: "BOQ Templates", icon: LayoutTemplate, testId: "sidebar-nav-boq-templates", perm: "quotes" },
        ],
      },
      {
        label: "Present",
        items: [
          { to: "/quotes", label: "Quotes", icon: FileSpreadsheet, testId: "sidebar-nav-quotes", perm: "quotes" },
          { to: "/renders", label: "Renders", icon: Images, testId: "sidebar-nav-renders", perm: "renders" },
        ],
      },
    ],
  },
  {
    key: "products",
    label: "Products",
    hint: "Trusted Suppliers catalogue & quotes",
    icon: Boxes,
    home: "/products",
    testId: "nav-rail-group-products",
    sections: [
      {
        label: "Catalogue",
        items: [{ to: "/products", label: "Trusted Suppliers", icon: Boxes, testId: "sidebar-nav-products", perm: "boq" }],
      },
    ],
  },
  {
    key: "contracts",
    label: "Contracts",
    hint: "Contract automation & e-signing",
    icon: FileSignature,
    home: "/contracts",
    testId: "nav-rail-group-contracts",
    sections: [
      {
        label: "Automation",
        items: [{ to: "/contracts", label: "Contracts", icon: ScrollText, testId: "sidebar-nav-contracts", perm: "crm" }],
      },
    ],
  },
  {
    key: "deliver",
    label: "Jobs",
    hint: "Active sites, schedule & paperwork",
    icon: Hammer,
    home: "/jobs",
    testId: "nav-rail-group-deliver",
    sections: [
      {
        label: "Run the job",
        items: [
          { to: "/jobs", label: "Jobs", icon: FolderKanban, testId: "sidebar-nav-jobs", perm: "jobs" },
          { to: "/schedule", label: "Schedule & Gantt", icon: CalendarDays, testId: "sidebar-nav-schedule", perm: "schedule" },
          { to: "/assignments", label: "Assignments", icon: UserCog, testId: "sidebar-nav-assignments", perm: "jobs" },
          { to: "/field", label: "Field View", icon: Smartphone, testId: "sidebar-nav-field", perm: "jobs", badge: "field" },
          { to: "/site-diary", label: "Site Diary", icon: NotebookPen, testId: "sidebar-nav-site-diary", perm: "jobs" },
        ],
      },
      {
        label: "Paperwork",
        items: [
          { to: "/job-quotes", label: "Job Quotes", icon: Mails, testId: "sidebar-nav-job-quotes", perm: "jobs" },
          { to: "/rfis", label: "RFIs", icon: FileQuestion, testId: "sidebar-nav-rfis", perm: "jobs" },
          { to: "/notices", label: "Notices", icon: FileSignature, testId: "sidebar-nav-notices", perm: "jobs" },
          { to: "/deliveries", label: "Deliveries", icon: Truck, testId: "sidebar-nav-deliveries", perm: "deliveries" },
        ],
      },
      {
        label: "Reference",
        items: [{ to: "/code-expert", label: "Code Expert", icon: BookOpenCheck, testId: "sidebar-nav-code-expert", perm: "jobs" }],
      },
    ],
  },
  {
    key: "money",
    label: "Financials",
    hint: "Income, OPEX & the CFO view",
    icon: Wallet,
    home: "/financials",
    testId: "nav-rail-group-money",
    sections: [
      {
        label: "Track",
        items: [
          { to: "/cashflow", label: "Cash Flow", icon: Banknote, testId: "sidebar-nav-cashflow", badge: "cashflow_attention", perm: "cashflow" },
          { to: "/progress-claims", label: "Progress Claims", icon: Milestone, testId: "sidebar-nav-progress-claims", perm: "cashflow" },
          { to: "/financials", label: "Financials", icon: Table2, testId: "sidebar-nav-financials", perm: "cashflow" },
          { to: "/transactions", label: "Transactions", icon: ArrowLeftRight, testId: "sidebar-nav-transactions", badge: "transactions", perm: "cashflow" },
        ],
      },
      {
        label: "Procurement",
        items: [
          { to: "/invoice-inbox", label: "Invoice Inbox", icon: Inbox, testId: "sidebar-nav-invoice-inbox", perm: "cashflow" },
          { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart, testId: "sidebar-nav-purchase-orders", perm: "purchase_orders" },
          { to: "/quote-comparison", label: "Quote Comparison", icon: Scale, testId: "sidebar-nav-quote-comparison", perm: "purchase_orders" },
          { to: "/cost-base", label: "Cost Base", icon: Database, testId: "sidebar-nav-cost-base", perm: "cost_base" },
        ],
      },
      {
        label: "Labour",
        items: [
          { to: "/timesheets", label: "Timesheets", icon: Clock, testId: "sidebar-nav-timesheets", perm: "jobs" },
        ],
      },
    ],
  },
  {
    key: "partners",
    label: "Partners",
    hint: "Contractors, suppliers & consultants",
    icon: Handshake,
    home: "/contractors",
    testId: "nav-rail-group-partners",
    sections: [
      {
        items: [
          { to: "/contractors", label: "Contractors", icon: HardHat, testId: "sidebar-nav-contractors", perm: "contractors" },
          { to: "/suppliers", label: "Suppliers", icon: Package, testId: "sidebar-nav-suppliers", perm: "suppliers" },
          { to: "/surveyors", label: "Surveyors", icon: FileCheck2, testId: "sidebar-nav-surveyors", perm: "surveyors" },
          { to: "/consultants", label: "Consultants", icon: DraftingCompass, testId: "sidebar-nav-consultants", perm: "surveyors" },
          { to: "/portal-access", label: "Portal access", icon: KeyRound, testId: "sidebar-nav-portal-access", perm: "contractors" },
        ],
      },
      {
        label: "People",
        items: [
          // HR & payroll carries TFNs, bank details and pay — admins (owners) only
          { to: "/hr", label: "HR", icon: Users, testId: "sidebar-nav-hr", adminOnly: true },
        ],
      },
    ],
  },
];

// Contractors are view-only and restricted to their assigned jobs
const CONTRACTOR_NAV = ["/", "/jobs"];

// Rail geometry (kept in one place so the content offset always matches).
// The rail is "expanded" (icon + label) by default and can be collapsed to icons only;
// a collapsed rail temporarily widens on hover without shifting the page content.
const RAIL_W_COLLAPSED = 64;
const RAIL_W_EXPANDED = 192;
const PANEL_W = 224;

// Persisted per-device: which group is open, whether the panel is pinned, rail expanded
const LS_GROUP = "bf.nav.group";
const LS_PINNED = "bf.nav.pinned";
const LS_RAIL = "bf.nav.railExpanded";

const readLS = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};
const writeLS = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage may be unavailable (private mode) — fail silently
  }
};

// True when this user may see the given permission module
export const hasPerm = (user, perm) => {
  if (!perm || !user) return true;
  if (user.role !== "team_member") return true;
  const perms = user.permissions || {};
  return perms[perm] !== false;
};

// Nav items flagged `adminOnly` (HR & payroll) are only offered to organisation admins
export const canSeeItem = (user, item) => {
  if (item.adminOnly && !isAdminLike(user)) return false; // Phase 191 — read-only viewers see everything admins see
  return hasPerm(user, item.perm);
};

const itemMatches = (item, pathname) =>
  item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);

// Which group owns the current route (null for Settings / Help / unknown)
const groupForPath = (pathname) => {
  for (const group of NAV_GROUPS) {
    for (const section of group.sections) {
      if (section.items.some((item) => itemMatches(item, pathname))) return group.key;
    }
  }
  return null;
};

// Groups → sections → items visible to this user (empty groups are dropped)
const useVisibleGroups = () => {
  const { user } = useAuth();
  return useMemo(() => {
    const isContractor = user?.role === "contractor";
    return NAV_GROUPS.map((group) => {
      const sections = group.sections
        .map((section) => ({
          ...section,
          items: isContractor
            ? section.items.filter((i) => CONTRACTOR_NAV.includes(i.to))
            : section.items.filter((i) => canSeeItem(user, i)),
        }))
        .filter((section) => section.items.length > 0);
      return { ...group, sections };
    }).filter((group) => group.sections.length > 0);
  }, [user]);
};

const groupBadgeTotal = (group, badges) =>
  group.sections.reduce(
    (sum, section) => sum + section.items.reduce((s, item) => s + (item.badge ? badges?.[item.badge] || 0 : 0), 0),
    0
  );

// The department's main page for this user: its declared `home` when they may see it,
// otherwise the first page in the group they do have access to.
const groupHome = (group) => {
  const items = group.sections.flatMap((s) => s.items);
  return (items.find((i) => i.to === group.home) || items[0])?.to || group.home || "/";
};

const Brand = ({ testId = "sidebar-brand-logo", className = "h-14" }) => (
  <div className="px-2">
    <BuilderLogo className={className} testId={testId} />
  </div>
);

const NAV_ITEM_BASE =
  "relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const NAV_ITEM_ACTIVE =
  "bg-accent text-accent-foreground before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-primary";
const NAV_ITEM_IDLE = "text-muted-foreground hover:bg-muted hover:text-foreground";
// Nested page under a parent item (e.g. Clients → Client Updates): indented, hairline connector, smaller icon.
const NAV_ITEM_SUB =
  "ml-4 pl-4 py-1.5 text-[13px] before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-px before:bg-border";

const NavItem = ({ to, label, icon: Icon, testId, end, badgeCount, onNavigate, sub = false }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onNavigate}
    data-testid={testId}
    data-sub={sub ? "true" : undefined}
    className={({ isActive }) =>
      `${NAV_ITEM_BASE} ${sub ? NAV_ITEM_SUB : ""} ${isActive ? (sub ? "bg-accent text-accent-foreground before:bg-primary before:w-[2px]" : NAV_ITEM_ACTIVE) : NAV_ITEM_IDLE}`
    }
  >
    <Icon className={sub ? "h-4 w-4 shrink-0" : "h-[18px] w-[18px] shrink-0"} />
    <span className="flex-1 truncate">{label}</span>
    {badgeCount > 0 && (
      <span
        className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums text-primary"
        data-testid={`${testId}-badge`}
      >
        {badgeCount > 99 ? "99+" : badgeCount}
      </span>
    )}
  </NavLink>
);

// Sub-sectioned list of one group's pages (used by the desktop panel)
const GroupSections = ({ group, badges, onNavigate, showLabels = true }) => (
  <div className="flex flex-col gap-3">
    {group.sections.map((section, idx) => (
      <div key={section.label || idx}>
        {showLabels && section.label && (
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">{section.label}</p>
        )}
        <div className="flex flex-col gap-0.5">
          {section.items.map((item) => (
            <NavItem key={item.to} {...item} badgeCount={item.badge ? badges?.[item.badge] || 0 : 0} onNavigate={onNavigate} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Full list, all groups — used by the mobile sheet where there is no rail
const NavList = ({ onNavigate, badges }) => {
  const { user } = useAuth();
  const groups = useVisibleGroups();
  const isContractor = user?.role === "contractor";
  return (
    <nav className="mt-4 flex flex-col gap-5 overflow-y-auto pr-0.5" data-testid="mobile-nav-list">
      {groups.map((group) => (
        <div key={group.key}>
          {!isContractor && (
            <p className="flex items-center gap-2 px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
              <group.icon className="h-3.5 w-3.5" /> {group.label}
            </p>
          )}
          <GroupSections group={group} badges={badges} onNavigate={onNavigate} showLabels={false} />
        </div>
      ))}
    </nav>
  );
};

const SettingsLink = ({ onNavigate, badges }) => {
  const { user } = useAuth();
  if (user?.role === "contractor" || !hasPerm(user, "settings")) return null;
  const warnCount = badges?.budget_warnings || 0;
  return (
    <NavLink
      to="/settings"
      onClick={onNavigate}
      data-testid="sidebar-nav-settings"
      title={warnCount > 0 ? `${warnCount} budget warning${warnCount === 1 ? "" : "s"} waiting for review — Settings → Alerts` : undefined}
      className={({ isActive }) => `${NAV_ITEM_BASE} ${isActive ? NAV_ITEM_ACTIVE : NAV_ITEM_IDLE}`}
    >
      <Settings className="h-[18px] w-[18px]" />
      <span className="flex-1">Settings</span>
      {warnCount > 0 && (
        <span
          className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500/15 px-1.5 text-[11px] font-semibold tabular-nums text-red-400"
          data-testid="sidebar-nav-settings-badge"
        >
          {warnCount > 99 ? "99+" : warnCount}
        </span>
      )}
    </NavLink>
  );
};

// Help is available to every role — the always-on lifeline
const HelpLink = ({ onNavigate }) => (
  <NavLink
    to="/help"
    onClick={onNavigate}
    data-testid="sidebar-nav-help"
    className={({ isActive }) => `${NAV_ITEM_BASE} ${isActive ? NAV_ITEM_ACTIVE : NAV_ITEM_IDLE}`}
  >
    <LifeBuoy className="h-[18px] w-[18px]" />
    Help
  </NavLink>
);

// Landlord console — BuiltOS super-admins only (mobile sheet)
const LandlordLink = ({ onNavigate, badges }) => {
  const { user } = useAuth();
  if (!user?.is_superadmin) return null;
  const count = badges?.signup_new || 0;
  return (
    <NavLink
      to="/landlord"
      onClick={onNavigate}
      data-testid="mobile-nav-landlord"
      className={({ isActive }) => `${NAV_ITEM_BASE} ${isActive ? NAV_ITEM_ACTIVE : NAV_ITEM_IDLE}`}
    >
      <Building2 className="h-[18px] w-[18px]" />
      <span className="flex-1">Landlord console</span>
      {count > 0 && (
        <span
          className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums text-primary"
          data-testid="mobile-nav-landlord-badge"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </NavLink>
  );
};

// Super-admin organisation switcher — lives inside the account popover. Lists every
// organisation (fetched when opened) and switches with one click; the API issues a session
// bound to that organisation and the app reloads so nothing from the previous tenant lingers.
const OrgSwitcher = ({ onNavigate }) => {
  const { user, switchOrg } = useAuth();
  const { shortName } = useBranding();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState(null);
  const [error, setError] = useState("");
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    if (!user?.is_superadmin) return undefined;
    let alive = true;
    api
      .get("/superadmin/orgs")
      .then((res) => alive && setOrgs(res.data?.orgs || []))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Could not load organisations"));
    return () => {
      alive = false;
    };
  }, [user?.is_superadmin]);

  if (!user?.is_superadmin) return null;
  const acting = user.acting_org_id || "";
  const currentId = acting || user.home_org_id || user.org_id;

  const go = async (org) => {
    setSwitching(org.id);
    try {
      await switchOrg(org.is_home ? null : org.id);
    } catch {
      setSwitching(null);
    }
  };

  return (
    <div className="border-b border-border px-3 py-2" data-testid="rail-org-switcher">
      <div className="mb-1 flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">Organisation</p>
        {acting && (
          <span className="rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold text-primary" data-testid="rail-org-switcher-acting">
            Support mode
          </span>
        )}
      </div>
      <p className="truncate text-sm font-medium" data-testid="rail-org-switcher-current">
        {shortName || "…"}
      </p>
      <div className="mt-2 max-h-[180px] space-y-0.5 overflow-y-auto">
        {error ? (
          <p className="px-1 text-[11px] text-destructive">{error}</p>
        ) : !orgs ? (
          <p className="px-1 text-[11px] text-muted-foreground">Loading organisations…</p>
        ) : (
          orgs.map((org) => {
            const isCurrent = org.id === currentId;
            return (
              <button
                key={org.id}
                type="button"
                disabled={isCurrent || Boolean(switching)}
                onClick={() => go(org)}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  isCurrent ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                } disabled:cursor-default`}
                aria-current={isCurrent ? "true" : undefined}
                data-testid={`rail-org-switch-${org.id}`}
              >
                {switching === org.id ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                ) : org.is_home ? (
                  <Undo2 className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <ArrowRightLeft className="h-3.5 w-3.5 shrink-0" />
                )}
                <span className="min-w-0 flex-1 truncate">{org.short_name || org.name}</span>
                {org.is_home && <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">home</span>}
                {isCurrent && <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />}
              </button>
            );
          })
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="mt-1.5 h-8 w-full justify-start gap-2 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => {
          onNavigate?.();
          navigate("/landlord");
        }}
        data-testid="rail-org-switcher-landlord-link"
      >
        <Building2 className="h-3.5 w-3.5" /> Landlord console
      </Button>
    </div>
  );
};

const relBell = (iso) => {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins || 1}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

// Shared look for the square icon buttons that live on the rail
const RAIL_BTN_BASE =
  "relative flex h-11 w-11 items-center justify-center rounded-xl transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98]";
// Expanded rail: full-width row — fixed icon column, label, count pill aligned to the right edge.
// No overflow clipping here so the active accent bar can render; labels truncate themselves.
const RAIL_ROW_BASE =
  "relative flex h-11 w-full items-center gap-3 whitespace-nowrap rounded-xl pl-3 pr-2.5 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.99]";
const RAIL_BTN_IDLE = "text-muted-foreground hover:bg-muted hover:text-foreground";
// Active department: tinted row + 3px accent bar. Collapsed → bar sits in the gutter beside the square;
// expanded → bar hugs the row's own left edge (the row already spans the rail).
const RAIL_BTN_ACTIVE =
  "bg-primary/15 text-primary before:absolute before:-left-[10px] before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-primary";
const RAIL_ROW_ACTIVE =
  "bg-primary/15 text-primary before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r-full before:bg-primary";
const RAIL_BTN_SELECTED = "bg-muted text-foreground ring-1 ring-border";
// Pick the button geometry (and the matching active treatment) for the current rail mode
const railBtnClass = (wide, state) => `${wide ? RAIL_ROW_BASE : RAIL_BTN_BASE} ${wide && state === RAIL_BTN_ACTIVE ? RAIL_ROW_ACTIVE : state}`;

const RailTooltip = ({ label, hint, children, disabled = false }) => {
  if (disabled) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10} className="bg-popover text-popover-foreground border border-border shadow-lg">
        <p className="text-xs font-semibold">{label}</p>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </TooltipContent>
    </Tooltip>
  );
};

// Count bubble: pinned to the icon's corner on the collapsed rail, right-aligned pill on the expanded rail.
// Inline pills share one height/min-width and tabular digits so "2", "11" and "99+" line up down the rail.
const RailCount = ({ count, testId, wide = false, tone = "primary" }) => {
  if (!(count > 0)) return null;
  const colour = tone === "danger" ? "bg-red-500 text-white" : "bg-primary text-primary-foreground";
  return (
    <span
      className={`flex items-center justify-center rounded-full font-bold tabular-nums ${colour} ${
        wide
          ? "ml-auto h-5 min-w-[24px] shrink-0 px-1.5 text-[11px] leading-none"
          : "absolute -right-1 -top-1 h-[18px] min-w-[18px] px-1 text-[10px] leading-[18px]"
      }`}
      data-testid={testId}
      data-placement={wide ? "inline" : "corner"}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
};

// Label shown next to the icon when the rail is expanded (same optical baseline as the icon)
const RailLabel = ({ children, testId }) => (
  <span className="min-w-0 flex-1 truncate text-sm font-medium leading-5 animate-in fade-in-0 duration-150" data-testid={testId}>
    {children}
  </span>
);

// Bell: in-app notifications (RFI replies synced by BuiltBuddy, …) + unread homeowner replies across every job this user works
const NotificationsBell = ({ variant = "rail", wide = false, onOpenChange }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpenState] = useState(false);
  const setOpen = (v) => {
    setOpenState(v);
    onOpenChange?.(v);
  };
  const [data, setData] = useState({ total: 0, items: [] }); // client replies
  const notes = useNotifications(); // generic notifications (db.notifications) — shared store, one poll for bell + bubbles
  const [clearing, setClearing] = useState(false);

  const canSee = user && user.role !== "contractor" && user.role !== "client" && hasPerm(user, "jobs");

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    const load = () => {
      api
        .get("/notifications/client-replies")
        .then((res) => alive && setData(res.data || { total: 0, items: [] }))
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [canSee]);

  if (!canSee) return null;

  const total = (data.total || 0) + (notes.total || 0);

  const openItem = (item) => {
    setOpen(false);
    navigate(`/jobs/${item.job_id}?tab=client-updates`);
  };

  const openNote = async (n) => {
    setOpen(false);
    markNotificationRead(n.id);
    if (n.href) navigate(n.href);
    else if (n.job_id) navigate(`/jobs/${n.job_id}`);
  };

  const markAllRead = async () => {
    setClearing(true);
    try {
      await Promise.all([data.total ? api.post("/notifications/client-replies/read-all") : null, notes.total ? markAllNotificationsRead() : null]);
      setData({ total: 0, items: [] });
    } catch {
      // silent — next poll refreshes
    } finally {
      setClearing(false);
    }
  };

  const isRail = variant === "rail";
  const trigger = (
    <button
      aria-label={total > 0 ? `${total} unread notifications` : "Notifications"}
      className={
        isRail
          ? railBtnClass(wide, open ? RAIL_BTN_SELECTED : RAIL_BTN_IDLE)
          : "relative flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors duration-150 hover:border-primary/40 hover:text-foreground"
      }
      data-testid="client-replies-bell"
      data-unread={total}
    >
      <Bell className={isRail ? "h-5 w-5 shrink-0" : "h-4 w-4"} />
      {isRail && wide && <RailLabel testId="client-replies-bell-label">Notifications</RailLabel>}
      {isRail ? (
        <RailCount count={total} wide={wide} testId="client-replies-bell-count" />
      ) : (
        total > 0 && (
          <span
            className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-[18px] text-primary-foreground"
            data-testid="client-replies-bell-count"
          >
            {total > 99 ? "99+" : total}
          </span>
        )
      )}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {isRail ? (
        <RailTooltip label="Notifications" hint={total > 0 ? `${total} unread` : "All caught up"} disabled={wide}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </RailTooltip>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      <PopoverContent align={isRail ? "end" : "start"} side={isRail ? "right" : "bottom"} sideOffset={12} className="w-[340px] p-0" data-testid="client-replies-popover">
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Bell className="h-4 w-4 text-primary" /> Notifications
          </p>
          {total > 0 && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={markAllRead} disabled={clearing} data-testid="client-replies-mark-all-btn">
              <CheckCheck className="mr-1 h-3.5 w-3.5" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {total === 0 || (data.items.length === 0 && notes.items.length === 0) ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="client-replies-empty">
              Nothing new — all caught up.
            </p>
          ) : (
            <>
              {notes.items.length > 0 && (
                <div data-testid="notifications-list">
                  {notes.items.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => openNote(n)}
                      className="block w-full border-b border-border/60 px-3 py-2.5 text-left transition-colors duration-150 last:border-0 hover:bg-muted/40"
                      data-testid={`notification-${n.id}`}
                      data-kind={n.kind}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="flex min-w-0 items-center gap-1.5 truncate text-sm font-semibold">
                          {n.kind === "rfi_answered" ? <span className="h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--success))]" aria-hidden /> : null}
                          <span className="truncate">{n.title || "Update"}</span>
                        </p>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{relBell(n.created_at)}</span>
                      </div>
                      {n.job_name ? <p className="truncate text-xs text-muted-foreground">{n.job_name}{n.meta?.codes?.length ? ` · pin${n.meta.codes.length === 1 ? "" : "s"} ${n.meta.codes.join(", ")}` : ""}</p> : null}
                      {n.body ? (
                        <p className="mt-1 line-clamp-2 rounded-md bg-[hsl(var(--success)/0.08)] px-2 py-1 text-xs text-foreground" data-testid={`notification-body-${n.id}`}>
                          {n.body}
                        </p>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
              {data.items.length > 0 && (
                <div>
                  <p className="flex items-center gap-1.5 border-b border-border/60 bg-muted/20 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    <MessageCircle className="h-3 w-3" /> Client replies
                  </p>
                  {data.items.map((item) => (
                    <button
                      key={item.update_id}
                      onClick={() => openItem(item)}
                      className="block w-full border-b border-border/60 px-3 py-2.5 text-left transition-colors duration-150 last:border-0 hover:bg-muted/40"
                      data-testid={`client-reply-notification-${item.update_id}`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="truncate text-sm font-semibold">{item.job_name || "Job"}</p>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{relBell(item.latest_at)}</span>
                      </div>
                      <p className="truncate text-xs text-muted-foreground">
                        {item.update_title}
                        {item.unread_count > 1 ? ` · ${item.unread_count} new messages` : ""}
                      </p>
                      {item.replies?.length > 0 && (
                        <p className="mt-1 line-clamp-2 rounded-md bg-primary/10 px-2 py-1 text-xs text-foreground">
                          <span className="font-semibold">{item.replies[item.replies.length - 1].author_name}:</span>{" "}
                          {item.replies[item.replies.length - 1].message}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const roleLabel = (user) =>
  user.acting_org_id
    ? "BuiltOS support · acting admin"
    : user.is_superadmin
      ? "Admin · BuiltOS Landlord"
      : user.role === "admin"
        ? "Admin"
        : isReadOnlyUser(user)
          ? "Read only · view all"
          : user.role === "contractor"
          ? (user.portal_access_level === "view" ? "Contractor · view only" : "Contractor")
          : "Team member";

// Mobile sheet footer: name, role, sync status and logout
const UserCard = () => {
  const { user, logout } = useAuth();
  const [themeOpen, setThemeOpen] = useState(false);
  if (!user) return null;
  return (
    <div className="border-t border-border pt-4 px-2">
      {user.role !== "contractor" && hasPerm(user, "cashflow") && (
        <div className="mb-2 px-1">
          <LastSyncedBadge compact className="w-full justify-center" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
          {initials(user.name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{user.name}</p>
          <p className="truncate text-[11px] text-muted-foreground capitalize">{roleLabel(user)}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Theme settings"
          title="Theme settings"
          data-testid="mobile-theme-button"
          onClick={() => setThemeOpen(true)}
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
        >
          <Palette className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Log out"
          data-testid="logout-button"
          onClick={logout}
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>
      <ThemePickerDialog open={themeOpen} onOpenChange={setThemeOpen} />
    </div>
  );
};

// Rail avatar → popover with account details, Xero sync and logout
const UserMenu = ({ wide = false, onOpenChange }) => {
  const { user, logout } = useAuth();
  const [open, setOpenState] = useState(false);
  const setOpen = (v) => {
    setOpenState(v);
    onOpenChange?.(v);
  };
  const [themeOpen, setThemeOpen] = useState(false);
  const { meta: themeMeta, isAuto } = useColorTheme();
  if (!user) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <RailTooltip label={user.name} hint={roleLabel(user)} disabled={wide}>
        <PopoverTrigger asChild>
          <button
            aria-label="Account menu"
            className={railBtnClass(wide, open ? RAIL_BTN_SELECTED : RAIL_BTN_IDLE)}
            data-testid="rail-user-menu-trigger"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
              {initials(user.name)}
            </span>
            {wide && (
              <span className="min-w-0 flex-1 animate-in fade-in-0 duration-150" data-testid="rail-user-menu-label">
                <span className="block truncate text-sm font-medium leading-tight">{user.name}</span>
                <span className="block truncate text-[11px] leading-tight text-muted-foreground">{roleLabel(user)}</span>
              </span>
            )}
          </button>
        </PopoverTrigger>
      </RailTooltip>
      <PopoverContent side="right" align="end" sideOffset={12} className="w-[260px] p-0" data-testid="rail-user-menu">
        <div className="flex items-center gap-3 border-b border-border px-3 py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
            {initials(user.name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium" data-testid="rail-user-name">{user.name}</p>
            <p className="truncate text-[11px] text-muted-foreground" data-testid="rail-user-role">{roleLabel(user)}</p>
          </div>
        </div>
        {user.role !== "contractor" && hasPerm(user, "cashflow") && (
          <div className="border-b border-border px-3 py-2">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">Xero</p>
            <LastSyncedBadge className="w-full justify-start" />
          </div>
        )}
        {open && <OrgSwitcher onNavigate={() => setOpen(false)} />}
        <div className="p-1.5">
          <Button
            variant="ghost"
            data-testid="rail-theme-button"
            onClick={() => {
              setOpen(false);
              setThemeOpen(true);
            }}
            className="h-9 w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
          >
            <Palette className="h-4 w-4" />
            <span className="flex-1 text-left">Theme</span>
            <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="rail-theme-current">
              <span className="h-2.5 w-2.5 rounded-full border border-border" style={{ background: themeMeta.swatch.bg }} />
              {isAuto ? `Auto · ${themeMeta.name}` : themeMeta.name}
            </span>
          </Button>
          <Button
            variant="ghost"
            data-testid="logout-button"
            onClick={logout}
            className="h-9 w-full justify-start gap-2 text-muted-foreground hover:text-destructive"
          >
            <LogOut className="h-4 w-4" /> Log out
          </Button>
        </div>
      </PopoverContent>
      <ThemePickerDialog open={themeOpen} onOpenChange={setThemeOpen} />
    </Popover>
  );
};

// route prefix -> permission module, for direct-URL access guarding
const ROUTE_PERMS = [
  ["/approvals", "approvals"],
  ["/crm", "crm"],
  ["/contracts", "crm"],
  ["/clients", "clients"],
  ["/client-updates", "clients"],
  ["/quick-quote", "quick_quote"],
  ["/quotes", "quotes"],
  ["/rate-book", "quotes"],
  ["/boq-templates", "quotes"],
  ["/boq", "boq"],
  ["/products", "boq"],
  ["/renders", "renders"],
  ["/jobs", "jobs"],
  ["/site-diary", "jobs"],
  ["/schedule", "schedule"],
  ["/deliveries", "deliveries"],
  ["/cashflow", "cashflow"],
  ["/progress-claims", "cashflow"],
  ["/financials", "cashflow"],
  ["/invoice-inbox", "cashflow"],
  ["/purchase-orders", "purchase_orders"],
  ["/quote-comparison", "purchase_orders"],
  ["/timesheets", "jobs"],
  ["/cost-base", "cost_base"],
  ["/contractors", "contractors"],
  ["/portal-access", "contractors"],
  ["/suppliers", "suppliers"],
  ["/surveyors", "surveyors"],
  ["/settings", "settings"],
];

const PermGate = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Landlord console: BuiltOS super-admins only
  if ((pathname === "/landlord" || pathname.startsWith("/landlord/")) && !user?.is_superadmin) {
    return (
      <div className="rounded-xl border border-dashed border-border py-20 text-center" data-testid="landlord-access-denied">
        <ShieldOff className="mx-auto h-10 w-10 text-muted-foreground/50" />
        <p className="mt-4 font-medium">The Landlord console is for BuiltOS super-admins</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Only the platform owner&apos;s admins can see other organisations.</p>
      </div>
    );
  }
  // HR & payroll: organisation admins only (TFNs, bank details, pay)
  if ((pathname === "/hr" || pathname.startsWith("/hr/")) && !isAdminLike(user)) {
    return (
      <div className="rounded-xl border border-dashed border-border py-20 text-center" data-testid="hr-access-denied">
        <ShieldOff className="mx-auto h-10 w-10 text-muted-foreground/50" />
        <p className="mt-4 font-medium">HR &amp; payroll is for organisation admins</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Employee records, tax file numbers and pay details are restricted to admin accounts.</p>
      </div>
    );
  }
  const match = ROUTE_PERMS.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  if (match && !hasPerm(user, match[1])) {
    return (
      <div className="rounded-xl border border-dashed border-border py-20 text-center" data-testid="module-access-denied">
        <ShieldOff className="mx-auto h-10 w-10 text-muted-foreground/50" />
        <p className="mt-4 font-medium">This module is turned off for your account</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
          Your admin controls module access in Settings → Team. Ask them to switch it on if you need it.
        </p>
      </div>
    );
  }
  return <Outlet />;
};

// ---------------------------------------------------------------------------
// Desktop rail + contextual panel
// ---------------------------------------------------------------------------
// NavLink for the rail: active state computed here so we can hand Radix
// Tooltip/Slot a plain string className (Slot cannot merge a function).
const RailLink = ({ to, label, testId, wide = false, children }) => {
  const { pathname } = useLocation();
  const active = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <NavLink
      to={to}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={railBtnClass(wide, active ? RAIL_BTN_ACTIVE : RAIL_BTN_IDLE)}
      data-testid={testId}
    >
      {children}
    </NavLink>
  );
};

const DesktopNav = ({ badges, onOpenPalette, pinned, setPinned, railExpanded, setRailExpanded }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const groups = useVisibleGroups();
  const isContractor = user?.role === "contractor";
  const { shortName } = useBranding();

  const routeGroup = groupForPath(pathname);
  const [activeGroup, setActiveGroup] = useState(() => routeGroup || readLS(LS_GROUP, groups[0]?.key || "home"));
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const wrapRef = useRef(null);

  // Collapsed rail widens while hovered (or while one of its popovers is open) — an overlay,
  // so the page content never shifts. Small enter delay avoids flashing on a drive-by.
  const [hoverOpen, setHoverOpen] = useState(false);
  const [popoverHold, setPopoverHold] = useState(0);
  const hoverTimer = useRef(null);
  const railWide = railExpanded || hoverOpen || popoverHold > 0;
  const onRailEnter = () => {
    if (railExpanded) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoverOpen(true), 120);
  };
  const onRailLeave = () => {
    clearTimeout(hoverTimer.current);
    setHoverOpen(false);
  };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  useEffect(() => {
    if (railExpanded) setHoverOpen(false);
  }, [railExpanded]);
  const holdWhileOpen = (open) => setPopoverHold((n) => Math.max(0, n + (open ? 1 : -1)));

  // Follow navigation: the panel always shows the group you are working in
  useEffect(() => {
    if (routeGroup) setActiveGroup(routeGroup);
    setFlyoutOpen(false);
  }, [routeGroup, pathname]);

  useEffect(() => writeLS(LS_GROUP, activeGroup), [activeGroup]);

  // Unpinned flyout closes on outside click or Escape
  useEffect(() => {
    if (pinned || !flyoutOpen) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setFlyoutOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setFlyoutOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [pinned, flyoutOpen]);

  // Rail click = go to that department. Outside the group → land on its main page (the panel
  // follows the route). Already inside it → toggle its page list so you can hop between pages.
  const selectGroup = useCallback(
    (g) => {
      const inGroup = routeGroup === g.key;
      if (!inGroup) {
        setActiveGroup(g.key);
        navigate(groupHome(g));
        return;
      }
      if (pinned) {
        setActiveGroup(g.key);
        return;
      }
      if (flyoutOpen && activeGroup === g.key) {
        setFlyoutOpen(false);
      } else {
        setActiveGroup(g.key);
        setFlyoutOpen(true);
      }
    },
    [routeGroup, navigate, pinned, flyoutOpen, activeGroup]
  );

  const togglePinned = () => {
    setPinned((p) => !p);
    setFlyoutOpen(false);
  };

  const group = groups.find((g) => g.key === activeGroup) || groups[0];
  const panelVisible = Boolean(group) && (pinned || flyoutOpen);
  const showSettings = !isContractor && hasPerm(user, "settings");
  const warnCount = badges?.budget_warnings || 0;
  const signupNew = badges?.signup_new || 0;
  const railW = railWide ? RAIL_W_EXPANDED : RAIL_W_COLLAPSED;
  const panelLeft = railExpanded ? RAIL_W_EXPANDED : RAIL_W_COLLAPSED;
  const toggleRail = () => {
    setRailExpanded((v) => !v);
    setHoverOpen(false);
  };

  return (
    <div ref={wrapRef} className="hidden lg:block">
      {/* Rail: icon + label rows when expanded, icons only when collapsed */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-border bg-[hsl(var(--sidebar))] py-4 transition-[width] duration-200 ease-out ${
          railWide ? "items-stretch px-2.5" : "items-center"
        } ${!railExpanded && railWide ? "shadow-[12px_0_32px_-12px_rgba(0,0,0,0.35)]" : ""}`}
        style={{ width: railW }}
        onMouseEnter={onRailEnter}
        onMouseLeave={onRailLeave}
        data-testid="nav-rail"
        data-pinned={pinned ? "true" : "false"}
        data-expanded={railExpanded ? "true" : "false"}
        data-wide={railWide ? "true" : "false"}
      >
        {/* BuiltOS brand + collapse / expand toggle */}
        <div className={`flex items-center ${railWide ? "justify-between gap-1 px-0.5" : "flex-col gap-1.5"}`}>
          <RailTooltip label="BuiltOS" hint="Back to Today" disabled={railWide}>
            <button
              onClick={() => navigate("/")}
              className="flex items-center rounded-xl p-1 transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="BuiltOS — back to Today"
              data-testid="brand-home-link"
            >
              <BuiltOSWordmark compact={!railWide} testId="sidebar-brand-logo" />
            </button>
          </RailTooltip>
          <RailTooltip label={railExpanded ? "Collapse sidebar" : "Expand sidebar"} hint={railExpanded ? "Icons only — hover to peek" : "Keep labels visible"} disabled={railWide}>
            <button
              onClick={toggleRail}
              aria-label={railExpanded ? "Collapse sidebar to icons" : "Expand sidebar"}
              aria-pressed={railExpanded}
              className={`flex shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                railWide ? "h-8 w-8" : "h-8 w-8"
              }`}
              title={railExpanded ? "Collapse sidebar" : "Expand sidebar"}
              data-testid="nav-rail-toggle"
              data-state={railExpanded ? "expanded" : "collapsed"}
            >
              {railExpanded ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
            </button>
          </RailTooltip>
        </div>

        <nav className={`mt-4 flex flex-col gap-1 ${railWide ? "items-stretch" : "items-center"}`} aria-label="Workflow groups">
          {groups.map((g) => {
            const isRoute = routeGroup === g.key;
            const isSelected = panelVisible && group?.key === g.key;
            const count = groupBadgeTotal(g, badges);
            return (
              <RailTooltip key={g.key} label={g.label} hint={g.hint} disabled={railWide}>
                <button
                  onClick={() => selectGroup(g)}
                  aria-label={g.label}
                  aria-pressed={isSelected}
                  aria-current={isRoute ? "true" : undefined}
                  className={railBtnClass(railWide, isRoute ? RAIL_BTN_ACTIVE : isSelected ? RAIL_BTN_SELECTED : RAIL_BTN_IDLE)}
                  data-testid={g.testId}
                  data-active={isRoute ? "true" : "false"}
                  data-home={groupHome(g)}
                >
                  <g.icon className="h-5 w-5 shrink-0" />
                  {railWide && <RailLabel testId={`${g.testId}-label`}>{g.label}</RailLabel>}
                  <RailCount count={count} wide={railWide} testId={`${g.testId}-badge`} />
                </button>
              </RailTooltip>
            );
          })}
        </nav>

        <div className={`mt-auto flex flex-col gap-1 ${railWide ? "items-stretch" : "items-center"}`}>
          <RailTooltip label="Search" hint="⌘K · jump to anything" disabled={railWide}>
            <button onClick={onOpenPalette} aria-label="Search (⌘K)" className={railBtnClass(railWide, RAIL_BTN_IDLE)} data-testid="command-palette-trigger">
              <SearchIcon className="h-5 w-5 shrink-0" />
              {railWide && <RailLabel testId="command-palette-trigger-label">Search</RailLabel>}
              {railWide && <kbd className="ml-auto rounded border border-border bg-muted/60 px-1.5 text-[10px] font-medium text-muted-foreground">⌘K</kbd>}
            </button>
          </RailTooltip>
          <NotificationsBell variant="rail" wide={railWide} onOpenChange={holdWhileOpen} />
          <RailTooltip label="Help" hint="Guides and support" disabled={railWide}>
            <RailLink to="/help" label="Help" testId="sidebar-nav-help" wide={railWide}>
              <LifeBuoy className="h-5 w-5 shrink-0" />
              {railWide && <RailLabel testId="sidebar-nav-help-label">Help</RailLabel>}
            </RailLink>
          </RailTooltip>
          {user?.is_superadmin && (
            <RailTooltip label="Landlord console" hint={signupNew > 0 ? `${signupNew} new onboarding request${signupNew === 1 ? "" : "s"}` : "Organisations & onboarding"} disabled={railWide}>
              <RailLink to="/landlord" label="Landlord console" testId="sidebar-nav-landlord" wide={railWide}>
                <Building2 className="h-5 w-5 shrink-0" />
                {railWide && <RailLabel testId="sidebar-nav-landlord-label">Landlord console</RailLabel>}
                <RailCount count={signupNew} wide={railWide} testId="sidebar-nav-landlord-badge" />
              </RailLink>
            </RailTooltip>
          )}
          {showSettings && (
            <RailTooltip label="Settings" hint={warnCount > 0 ? `${warnCount} budget warning${warnCount === 1 ? "" : "s"} to review` : "Team, alerts, integrations"} disabled={railWide}>
              <RailLink to="/settings" label="Settings" testId="sidebar-nav-settings" wide={railWide}>
                <Settings className="h-5 w-5 shrink-0" />
                {railWide && <RailLabel testId="sidebar-nav-settings-label">Settings</RailLabel>}
                <RailCount count={warnCount} wide={railWide} tone="danger" testId="sidebar-nav-settings-badge" />
              </RailLink>
            </RailTooltip>
          )}
          <div className={`my-1 h-px bg-border ${railWide ? "mx-1" : "w-8"}`} />
          <UserMenu wide={railWide} onOpenChange={holdWhileOpen} />
        </div>
      </aside>

      {/* Contextual panel: only the selected group's pages */}
      {panelVisible && group && (
        <aside
          className={`fixed inset-y-0 z-30 flex flex-col border-r border-border bg-[hsl(var(--sidebar-panel))] px-3 py-5 ${
            pinned ? "" : "shadow-[12px_0_32px_-12px_rgba(0,0,0,0.45)]"
          } animate-in fade-in-0 slide-in-from-left-2 duration-200`}
          style={{ left: panelLeft, width: PANEL_W }}
          data-testid="nav-panel"
          data-group={group.key}
        >
          {/* Builder's brand — top of the sidebar */}
          <button
            onClick={() => navigate("/")}
            className="mb-4 flex w-full items-center rounded-lg px-1 py-1 text-left transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`${shortName || "Home"} — back to Today`}
            data-testid="nav-panel-brand-link"
          >
            <BuilderLogo className="h-11" testId="nav-panel-brand-logo" />
          </button>
          <div className="flex items-start justify-between gap-2 px-1 pb-4">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/90">
                <group.icon className="h-3.5 w-3.5" /> {group.label}
              </p>
              <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground" data-testid="nav-panel-hint">
                {group.hint}
              </p>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={togglePinned}
                  aria-label={pinned ? "Unpin panel" : "Pin panel open"}
                  aria-pressed={pinned}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="nav-panel-pin-toggle"
                >
                  {pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-popover text-popover-foreground border border-border text-[11px]">
                {pinned ? "Unpin — collapse to icons only" : "Pin — keep this panel open"}
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <GroupSections group={group} badges={badges} onNavigate={() => !pinned && setFlyoutOpen(false)} />
          </div>

          {!pinned && (
            <p className="mt-3 flex items-center gap-1 px-1 text-[10px] text-muted-foreground/60">
              <ChevronRight className="h-3 w-3" /> Esc to close
            </p>
          )}
          {!isContractor && hasPerm(user, "cashflow") && (
            <div className="mt-3 border-t border-border pt-3 px-1">
              <LastSyncedBadge compact className="w-full justify-center" />
            </div>
          )}
          <div className="mt-3 flex justify-center border-t border-border pt-3">
            <PoweredByBuiltOS testId="nav-panel-powered-by-builtos" />
          </div>
        </aside>
      )}
    </div>
  );
};

export const AppShell = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const [pinned, setPinnedState] = useState(() => readLS(LS_PINNED, true));
  const [railExpanded, setRailExpandedState] = useState(() => readLS(LS_RAIL, true));
  const { user } = useAuth();
  const { shortName, loaded: brandingLoaded } = useBranding();

  // Browser tab carries the builder's name inside the app (BuiltOS stays on public pages)
  useEffect(() => {
    if (!brandingLoaded) return undefined;
    const prev = document.title;
    document.title = shortName ? `${shortName} · BuiltOS` : "BuiltOS";
    return () => {
      document.title = prev;
    };
  }, [shortName, brandingLoaded]);

  // Pin preference is remembered per device
  const setPinned = useCallback((next) => {
    setPinnedState((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      writeLS(LS_PINNED, value);
      return value;
    });
  }, []);

  // Rail expanded / collapsed preference is remembered per device (default: expanded)
  const setRailExpanded = useCallback((next) => {
    setRailExpandedState((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      writeLS(LS_RAIL, value);
      return value;
    });
  }, []);

  // Content offset = rail (64 collapsed / 184 expanded) + panel (224) when pinned
  const navOffset = (railExpanded ? RAIL_W_EXPANDED : RAIL_W_COLLAPSED) + (pinned ? PANEL_W : 0);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Sidebar badge counts — refreshed every 60s (editors/admins only)
  useEffect(() => {
    if (!user || user.role === "contractor") return;
    let alive = true;
    const load = () =>
      api
        .get("/nav/badges")
        .then((res) => alive && setBadges(res.data || {}))
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [user]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="app-ambient flex min-h-screen">
        <BrandWatermark />
        <DesktopNav
          badges={badges}
          onOpenPalette={() => setPaletteOpen(true)}
          pinned={pinned}
          setPinned={setPinned}
          railExpanded={railExpanded}
          setRailExpanded={setRailExpanded}
        />

        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        <OnboardingChecklist />

        {/* Content offset follows the rail (expanded / collapsed) + the pinned panel; hover-peek never shifts it */}
        <div
          className="flex min-w-0 flex-1 flex-col transition-[padding] duration-200 ease-out lg:pl-[var(--nav-offset)]"
          style={{ "--nav-offset": `${navOffset}px` }}
          data-testid="app-content"
          data-nav-pinned={pinned ? "true" : "false"}
          data-nav-rail={railExpanded ? "expanded" : "collapsed"}
        >
          {/* Landlord working inside another organisation — impossible to miss, one click home */}
          <ActingBanner />
          {/* 106B — admins see a slim strip everywhere while the Xero refresh token is dead */}
          <XeroReconnectBanner variant="strip" />
          {/* 114 — admins see a slim strip everywhere while a Basiq bank connection needs re-authorising */}
          <BankFeedReconnectBanner />
          {/* Phase 191 — "Read Only — view all" accounts always know why nothing can be changed */}
          {isReadOnlyUser(user) && (
            <div className="flex items-center justify-center gap-2 border-b border-border bg-muted/60 px-4 py-1.5 text-xs text-muted-foreground" role="status" data-testid="read-only-banner">
              <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span><span className="font-semibold text-foreground">View only</span> — {READ_ONLY_MESSAGE.replace("View-only account — ", "")}</span>
            </div>
          )}
          {/* Mobile header */}
          <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-border bg-background/80 px-4 py-3 backdrop-blur">
            <Brand testId="mobile-header-brand-logo" className="h-10" />
            <div className="flex items-center gap-2">
              <NotificationsBell variant="compact" />
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Open menu" data-testid="mobile-menu-button">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] flex flex-col px-4 py-6">
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  <SheetDescription className="sr-only">Jump to any area of the app</SheetDescription>
                  <Brand testId="mobile-menu-brand-logo" />
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      onClick={() => {
                        setMobileOpen(false);
                        setPaletteOpen(true);
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                      data-testid="mobile-command-palette-trigger"
                    >
                      <SearchIcon className="h-3.5 w-3.5" />
                      <span className="flex-1 truncate text-left">Search…</span>
                    </button>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto">
                    <NavList badges={badges} onNavigate={() => setMobileOpen(false)} />
                  </div>
                  <div className="mt-2 space-y-1">
                    <HelpLink onNavigate={() => setMobileOpen(false)} />
                    <LandlordLink badges={badges} onNavigate={() => setMobileOpen(false)} />
                    <SettingsLink badges={badges} onNavigate={() => setMobileOpen(false)} />
                    <div className="pt-2">
                      <UserCard />
                    </div>
                    <div className="flex justify-center pt-2">
                      <PoweredByBuiltOS testId="mobile-powered-by-builtos" />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </header>

          <main className="flex-1">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
              <PermGate />
            </div>
          </main>
          {/* BuiltBuddy on every page — job pages render their own dock (launcher + panel); elsewhere this opens a job picker */}
          <BuiltBuddyGlobalLauncher />
        </div>
      </div>
    </TooltipProvider>
  );
};
